import {
  AddCircle,
  Assessment,
  CompareArrows,
  Dataset,
} from '@mui/icons-material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MapIcon from '@mui/icons-material/Map';
import {
  Box,
  LinearProgress,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useRouter } from '@pn/core/operations/router';
import React from 'react';
import { Link } from 'react-router-dom';
import { useIntelReportsStorage } from 'src/storage';
import { useUI } from './contexts/useUIState';

export type Page = {
  title: string;
  icon: React.ReactNode;
  path?: string;
  component?: React.ReactNode;
  subPaths?: Page[];
  addDividers?: boolean;
};

export const baseDashboardURL = '/dashboards';

export const activityMapURL = baseDashboardURL + '/map';
export const companiesURL = baseDashboardURL + '/companies';
export const companyURL = companiesURL + '/:companyId';
export const chartsURL = baseDashboardURL + '/charts';
export const reportsURL = baseDashboardURL + '/reports';
export const reportURL = reportsURL + '/:reportId/';
export const listReportsURL = baseDashboardURL + '/list_report';
export const listReportURL = listReportsURL + '/:workspaceItemId';
export const baLookupURL = baseDashboardURL + '/ba-lookup';
export const mergersAndAcquisitionsURL = baseDashboardURL + '/m-and-a-database';

export const pages: Page[] = [
  {
    title: 'Activity Map',
    icon: <MapIcon />,
    path: activityMapURL,
  },
  {
    title: 'Companies',
    path: companiesURL,
    icon: <ListAltIcon />,
    addDividers: true,
  },
  {
    title: 'Compare Charts',
    path: chartsURL,
    icon: <CompareArrows />,
  },
  {
    title: 'Reports',
    icon: <Assessment />,
    path: reportsURL,
    component: <ReportsNavigation />,
    addDividers: true,
  },
  // {
  //   title: 'Well List Report',
  //   icon: <QueryStats />,
  //   path: base + 'list-report',
  // },
  {
    title: 'M&A',
    icon: <Dataset />,
    path: mergersAndAcquisitionsURL,
    addDividers: true,
  },
  {
    title: 'BA Lookup',
    icon: <ManageSearchIcon />,
    path: baLookupURL,
  },
];

function ReportsNavigation() {
  const { pinnedReports, isFetching } = useIntelReportsStorage();
  const { pathname } = useRouter();
  const { isSidePanelExpanded } = useUI();

  const isEditingOrCreatingReport = pathname.endsWith('reports/');

  if (isFetching) {
    return <LinearProgress />;
  }

  return (
    <>
      {pinnedReports.map((report) => {
        const selected = pathname.includes(report.id);
        return (
          <Tooltip
            key={report.id}
            title={'Reports: ' + report.filters.name}
            arrow
            placement={'right'}
            disableHoverListener={isSidePanelExpanded}
          >
            <ListItemButton
              component={Link}
              selected={selected}
              to={reportsURL + '/' + report.id}
              sx={{
                pl: isSidePanelExpanded ? 7.5 : 0.5,
                pr: 0.5,
                py: 0.5,
                mb: 0.5,
                borderRadius: 1,
              }}
            >
              {isSidePanelExpanded ? (
                <ListItemText
                  sx={(theme) => ({
                    '& .MuiListItemText-primary': {
                      fontWeight: selected
                        ? theme.typography.fontWeightBold
                        : theme.typography.fontWeightRegular,
                    },
                  })}
                  primary={report.filters.name}
                />
              ) : (
                <Box
                  sx={(theme) => ({
                    width: selected ? 24 : 20,
                    height: selected ? 24 : 20,
                    boxSizing: 'content-box',
                    border: selected
                      ? undefined
                      : `2px solid ${theme.palette.text.secondary}`,
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: selected
                      ? theme.palette.text.primary
                      : 'unset',
                    lineHeight: `${selected ? 22 : 18}px`,
                    textAlign: 'center',
                  })}
                >
                  <Box
                    component={'span'}
                    sx={(theme) => ({
                      fontFamily: theme.typography.fontFamily,
                      fontSize: 12,
                      fontWeight: 500,
                      color: selected
                        ? theme.palette.getContrastText(
                            theme.palette.text.secondary
                          )
                        : theme.palette.text.secondary,
                    })}
                  >
                    {report.filters.name[0]}
                  </Box>
                </Box>
              )}
            </ListItemButton>
          </Tooltip>
        );
      })}
      <Tooltip
        arrow
        placement={'right'}
        title={'New Dashboard'}
        disableHoverListener={isSidePanelExpanded}
      >
        <ListItemButton
          sx={(theme) => ({
            height: theme.spacing(4),
            pl: isSidePanelExpanded ? 7.5 : 0.5,
            py: 0.25,
            mb: 0.5,
            borderRadius: 1,
          })}
          component={Link}
          selected={isEditingOrCreatingReport}
          to={baseDashboardURL + '/reports?openCreateReportDialog=true'}
        >
          {isSidePanelExpanded && <ListItemText primary={'New Report'} />}
          <AddCircle sx={{ mr: 0.25 }} />
        </ListItemButton>
      </Tooltip>
    </>
  );
}
